import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut,
  FiMaximize2, FiDownload, FiAlertCircle, FiLoader,
} = FiIcons;

const usePdfPages = (pdfBase64) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfjsLib, setPdfjsLib] = useState(null);

  useEffect(() => {
    if (window.pdfjsLib) {
      setPdfjsLib(window.pdfjsLib);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      setPdfjsLib(window.pdfjsLib);
    };
    script.onerror = () => setError('Failed to load PDF renderer.');
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!pdfjsLib || !pdfBase64) return;
    let cancelled = false;

    const renderPages = async () => {
      setLoading(true);
      setError('');
      try {
        const binary = atob(pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        const rendered = [];

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.8 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;
          rendered.push({
            dataUrl: canvas.toDataURL('image/jpeg', 0.92),
            width: viewport.width,
            height: viewport.height,
          });
        }

        if (!cancelled) {
          setPages(rendered);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Could not render PDF: ' + err.message);
          setLoading(false);
        }
      }
    };

    renderPages();
    return () => { cancelled = true; };
  }, [pdfjsLib, pdfBase64]);

  return { pages, loading, error };
};

const PdfDevicePreview = ({ pdfBase64, downloadUrl }) => {
  const { pages, loading, error } = usePdfPages(pdfBase64);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState(1);

  const goToPage = (dir) => {
    const next = currentPage + dir;
    if (next < 0 || next >= pages.length || isFlipping) return;
    setFlipDir(dir);
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentPage(next);
      setIsFlipping(false);
    }, 300);
  };

  const jumpToPage = (idx) => {
    if (idx === currentPage || isFlipping) return;
    setFlipDir(idx > currentPage ? 1 : -1);
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentPage(idx);
      setIsFlipping(false);
    }, 300);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-slate-200">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
          <SafeIcon icon={FiLoader} className="text-4xl text-primary" />
        </motion.div>
        <p className="text-slate-500 font-medium mt-4">Rendering PDF pages...</p>
        <p className="text-xs text-slate-400 mt-1">This may take a moment for large books</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-slate-200">
        <SafeIcon icon={FiAlertCircle} className="text-4xl text-amber-500 mb-4" />
        <h4 className="font-bold text-slate-700 mb-2">PDF Render Error</h4>
        <p className="text-sm text-slate-500 max-w-sm text-center mb-6">{error}</p>
        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-secondary text-white font-bold text-sm"
          >
            <SafeIcon icon={FiDownload} />
            <span>Download PDF Instead</span>
          </a>
        )}
      </div>
    );
  }

  if (pages.length === 0) return null;

  const page = pages[currentPage];
  const progress = ((currentPage + 1) / pages.length) * 100;

  return (
    <div className={`flex flex-col items-center ${fullscreen ? 'fixed inset-0 z-50 bg-slate-900 p-6 overflow-auto' : ''}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between w-full max-w-2xl mb-4 px-2">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
          Print PDF Preview — {pages.length} pages
        </span>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setZoom(z => Math.max(0.5, z - 0.15))}
            className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-primary transition-all"
            title="Zoom out"
          >
            <SafeIcon icon={FiZoomOut} />
          </button>
          <span className="text-xs font-bold text-slate-600 w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(z => Math.min(2, z + 0.15))}
            className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-primary transition-all"
            title="Zoom in"
          >
            <SafeIcon icon={FiZoomIn} />
          </button>
          <button
            onClick={() => setFullscreen(f => !f)}
            className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-primary transition-all"
          >
            <SafeIcon icon={FiMaximize2} />
          </button>
        </div>
      </div>

      {/* Page viewer */}
      <div className="relative flex items-center justify-center">
        {/* Prev button */}
        <button
          onClick={() => goToPage(-1)}
          disabled={currentPage === 0}
          className="absolute left-0 -translate-x-12 z-10 p-3 rounded-full bg-white border border-slate-200 shadow-lg text-slate-600 hover:text-primary disabled:opacity-30 transition-all"
        >
          <SafeIcon icon={FiChevronLeft} className="text-xl" />
        </button>

        {/* Page */}
        <div
          className="relative shadow-2xl rounded-sm overflow-hidden"
          style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.35)' }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, rotateY: flipDir * 8, scale: 0.97 }}
              animate={{ opacity: 1, rotateY: 0, scale: 1 }}
              exit={{ opacity: 0, rotateY: -flipDir * 8, scale: 0.97 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <img
                src={page.dataUrl}
                alt={`Page ${currentPage + 1}`}
                style={{
                  width: `${page.width * zoom * 0.35}px`,
                  height: 'auto',
                  display: 'block',
                  imageRendering: 'high-quality',
                  background: '#fff',
                }}
                draggable={false}
              />
            </motion.div>
          </AnimatePresence>

          {/* Page curl effect */}
          <div
            className="absolute bottom-0 right-0 w-8 h-8 pointer-events-none"
            style={{
              background: 'linear-gradient(225deg,#e2e8f0 45%,transparent 55%)',
              borderTopLeftRadius: '100%',
            }}
          />
        </div>

        {/* Next button */}
        <button
          onClick={() => goToPage(1)}
          disabled={currentPage === pages.length - 1}
          className="absolute right-0 translate-x-12 z-10 p-3 rounded-full bg-white border border-slate-200 shadow-lg text-slate-600 hover:text-primary disabled:opacity-30 transition-all"
        >
          <SafeIcon icon={FiChevronRight} className="text-xl" />
        </button>
      </div>

      {/* Progress */}
      <div className="mt-6 w-full max-w-sm">
        <div className="flex items-center justify-between text-xs text-slate-500 font-medium mb-2">
          <span>Page {currentPage + 1} of {pages.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-1.5">
          <motion.div
            className="h-full bg-secondary rounded-full"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Full scrollable thumbnail strip — all pages */}
      {pages.length > 1 && (
        <div className="mt-4 w-full max-w-2xl">
          <div
            className="flex space-x-2 overflow-x-auto pb-3"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}
          >
            {pages.map((p, i) => (
              <button
                key={i}
                onClick={() => jumpToPage(i)}
                title={`Page ${i + 1}`}
                className={`flex-shrink-0 rounded border-2 overflow-hidden transition-all ${
                  i === currentPage
                    ? 'border-primary shadow-lg scale-105'
                    : 'border-transparent hover:border-slate-300 opacity-70 hover:opacity-100'
                }`}
              >
                <img
                  src={p.dataUrl}
                  alt={`Page ${i + 1}`}
                  style={{ width: '48px', height: 'auto', display: 'block' }}
                />
                <div className="text-center text-[9px] font-bold text-slate-400 py-0.5 bg-white">
                  {i + 1}
                </div>
              </button>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-1">
            Scroll thumbnail strip to browse all {pages.length} pages
          </p>
        </div>
      )}
    </div>
  );
};

export default PdfDevicePreview;